/**
 * <sunday-m-inside>: "What's inside." (sections/sunday-m-inside.liquid), ported from the mockup (v62).
 * The mattress with its cover pulled back at one corner; layers from the child JSON (the layer records), drawn
 * thick enough to read on a phone (every layer at least 26 units, so the dots never touch), a dot per layer and one
 * on the cover. A dot opens its card: under the dot with a dashed leader on a phone and tablet (full width), beside
 * the drawing from 960px. The card opens with a swatch of the layer as drawn, then a label/value list of the
 * record's facts ("fx": label::value||label::value). All dots (layers and cover) sit on one vertical line; each has
 * a 44 px hit area (the nearest dot to a tap wins). Colours are the theme's drawing tokens.
 */
import { esc } from '@sunday/util';

const INK = 'var(--sd-ink)';
const FILL = { latex: 'var(--sd-art-pale-gold)', memory: 'color-mix(in srgb, var(--sd-art-gold) 62%, var(--sd-art-khaki))', foam: 'var(--sd-art-cut-top)' };
const COVER = 'var(--sd-art-cover)';
const shade = (c, k) => `color-mix(in srgb, ${c} ${Math.round(k * 100)}%, var(--sd-art-black))`;
const fillOf = (l) => FILL[l.kind] || FILL.foam;
let uid = 0;

function bed(L, aria, label) {
  const Tt = L.reduce((a, l) => a + Number(l.in), 0);
  const K = Math.min(14, 110 / Tt);
  const hs = L.map((l) => Math.max(26, Number(l.in) * K));
  const T = hs.reduce((a, b) => a + b, 0);
  const VW = 500, FLx = 22, FRx = 394, dx = 70, dy = -30, FLy = 64, FRy = FLy;
  const BLx = FLx + dx, BLy = FLy + dy, BRx = FRx + dx, BRy = FRy + dy;
  const px = (x, y) => x.toFixed(1) + ' ' + y.toFixed(1);
  const fills = fillOf;
  const cw = 160, cd = 0.66;
  const P1 = [FRx - cw, FLy], P3 = [FRx - cw + dx * cd, FLy + dy * cd], P2 = [FRx + dx * cd, FLy + dy * cd], F = [FRx, FRy];
  const id = 'sdmin' + uid++;
  const sil = `M${px(FLx, FLy)}L${px(BLx, BLy)}L${px(BRx, BRy)}L${px(BRx, BRy + T)}L${px(FRx, FRy + T)}L${px(FLx, FLy + T)}Z`;
  const reg = `M${px(P1[0], P1[1])}L${px(P3[0], P3[1])}L${px(P2[0], P2[1])}L${px(P2[0], P2[1] + T)}L${px(FRx, FRy + T)}L${px(P1[0], P1[1] + T)}Z`;
  const knit = 'color-mix(in srgb, var(--sd-art-stone) 55%, var(--sd-art-khaki))';
  let g = `<defs><clipPath id="${id}c"><path clip-rule="evenodd" d="${sil}${reg}"/></clipPath><clipPath id="${id}r"><path d="${reg}"/></clipPath>
  <pattern id="${id}k" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="${knit}"/><path d="M1 0V4" stroke="${shade(knit, 0.92)}" stroke-width="1"/></pattern>
  <pattern id="${id}k2" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="${shade(knit, 0.9)}"/><path d="M1 0V4" stroke="${shade(knit, 0.82)}" stroke-width="1"/></pattern>
  <pattern id="${id}p" width="9" height="9" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1" fill="var(--sd-art-black)" opacity=".09"/><circle cx="6.5" cy="6" r=".8" fill="var(--sd-art-black)" opacity=".07"/></pattern></defs>`;
  g += `<ellipse cx="${(FLx + BRx) / 2}" cy="${FLy + T + 14}" rx="${(BRx - FLx) / 2 + 4}" ry="10" fill="var(--sd-art-sand)" opacity=".6"/>`;
  let y = 0;
  const bands = [];
  L.forEach((l, i) => {
    const h = hs[i], c = fills(l);
    bands.push(`<path d="M${px(FLx, FLy + y)}H${FRx}V${FLy + y + h}H${FLx}Z" fill="${c}"/><path d="M${px(FLx, FLy + y)}H${FRx}V${FLy + y + h}H${FLx}Z" fill="url(#${id}p)"/>`);
    bands.push(`<path d="M${px(FRx, FRy + y)}L${px(BRx, BRy + y)}V${BRy + y + h}L${px(FRx, FRy + y + h)}Z" fill="${shade(c, 0.9)}"/>`);
    y += h;
  });
  g += `<g>${bands.join('')}<path d="M${px(FLx, FLy)}L${px(BLx, BLy)}L${px(BRx, BRy)}L${px(FRx, FRy)}Z" fill="${fills(L[0])}"/></g>`;
  let joins = '';
  y = 0;
  L.forEach((l, i) => {
    if (i) joins += `<path d="M${px(FLx, FLy + y)}H${FRx}L${px(BRx, BRy + y)}" fill="none" stroke="${INK}" stroke-width="1.4" stroke-opacity=".5"/>`;
    y += hs[i];
  });
  g += `<g clip-path="url(#${id}r)">${joins}<path d="M${px(P1[0], P1[1])}L${px(P3[0], P3[1])}L${px(P2[0], P2[1])}L${px(F[0], F[1])}Z" fill="${shade(fills(L[0]), 0.97)}"/></g>`;
  let quilt = '';
  for (let i = 1; i < 13; i++) {
    const f = i / 13;
    quilt += `M${px(FLx + (FRx - FLx) * f, FLy)}L${px(BLx + (BRx - BLx) * f, BLy)}`;
  }
  for (let i = 1; i < 5; i++) {
    const f = i / 5;
    quilt += `M${px(FLx + dx * f, FLy + dy * f)}H${FRx + dx * f}`;
  }
  const lbx = FLx + 120, lby = FLy + T / 2;
  g += `<g clip-path="url(#${id}c)"><path d="M${px(FLx, FLy)}H${FRx}V${FLy + T}H${FLx}Z" fill="url(#${id}k)"/><path d="M${px(FRx, FRy)}L${px(BRx, BRy)}V${BRy + T}L${px(FRx, FRy + T)}Z" fill="url(#${id}k2)"/><path d="M${px(FLx, FLy)}L${px(BLx, BLy)}L${px(BRx, BRy)}L${px(FRx, FRy)}Z" fill="${COVER}"/>
  <path d="${quilt}" stroke="var(--sd-art-khaki)" stroke-width=".9" fill="none"/>
  <path d="M${px(FLx, FLy + 1.5)}H${FRx}L${px(BRx, BRy + 1.5)}" stroke="var(--sd-card)" stroke-width="3" fill="none" opacity=".9"/>
  <rect x="${lbx}" y="${lby - 17}" width="14" height="34" rx="1.5" fill="color-mix(in srgb, var(--sd-link) 55%, var(--sd-art-night))"/><text transform="translate(${lbx + 10} ${lby + 13}) rotate(-90)" font-weight="600" font-size="11" fill="var(--sd-card)" letter-spacing=".4">${esc(label)}</text></g>`;
  g += `<path d="M${px(P1[0], P1[1] + T)}V${P1[1]}L${px(P3[0], P3[1])}L${px(P2[0], P2[1])}" fill="none" stroke="${INK}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>
  <path d="M${px(P3[0], P3[1])}L${px(P3[0] - 30, P3[1] + 20)}L${px(P1[0] - 2, P1[1] - 2)}Z" fill="${COVER}" stroke="${INK}" stroke-width="1.8" stroke-linejoin="round"/>`;
  g += `<path d="${sil}" fill="none" stroke="${INK}" stroke-width="2" stroke-linejoin="round"/><path d="M${px(FLx, FLy)}H${FRx}L${px(BRx, BRy)}M${px(FRx, FRy)}V${FRy + T}" fill="none" stroke="${INK}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
  const dot = (k, cx, cy, n) => `<g class="sd-min-hs" data-hs="${k}" tabindex="0" role="button" aria-label="${esc(n)}"><circle class="sd-min-hit" cx="${cx}" cy="${cy}" r="26" fill="transparent" style="pointer-events:all"/><circle class="sd-min-ring" cx="${cx}" cy="${cy}" r="10"/><circle class="sd-min-ring sd-min-r2" cx="${cx}" cy="${cy}" r="10"/><circle class="sd-min-dt" cx="${cx}" cy="${cy}" r="8"/></g>`;
  y = 0;
  const cxm = (P1[0] + FRx) / 2;
  g += L.map((l, i) => {
    const cy = FLy + y + hs[i] / 2;
    y += hs[i];
    return dot('L' + i, cxm, cy, l.title);
  }).join('');
  return { g, H: FLy + T + 30, VW, cover: (c) => dot('cover', cxm, FLy + dy / 2, c.title) };
}

class SundayMInside extends HTMLElement {
  connectedCallback() {
    if (this.done) return;
    this.done = true;
    const all = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    const L = all.filter((x) => !x.cover);
    const cover = all.find((x) => x.cover);
    const b = bed(L, this.dataset.aria, this.dataset.label || '');
    const facts = (fx) => {
      const rows = String(fx || '')
        .split('||')
        .map((r) => r.split('::'))
        .filter((r) => r.length === 2 && r[0].trim() && r[1].trim());
      return rows.length ? `<dl class="sd-min-fx">${rows.map((r) => `<div><dt>${esc(r[0])}</dt><dd>${esc(r[1])}</dd></div>`).join('')}</dl>` : '';
    };
    const pops = all
      .map((x, i) => {
        const k = x.cover ? 'cover' : 'L' + i;
        const sw = x.cover ? COVER : fillOf(x);
        return `<div class="sd-min-pop" data-for="${k}"${k === 'L0' ? '' : ' hidden'}><i class="sd-min-ar"></i><div class="sd-min-hd"><span class="sd-min-sw" style="background:${sw}" aria-hidden="true"></span><span><b>${esc(x.title)}</b>${x.note ? `<span class="sd-min-nt">${esc(x.note)}</span>` : ''}</span></div>${facts(x.fx)}</div>`;
      })
      .join('');
    this.innerHTML = `<svg class="sd-min-bed" viewBox="0 0 ${b.VW} ${b.H}" role="img" aria-label="${esc(this.dataset.aria || '')}">${b.g}${cover ? b.cover(cover) : ''}</svg><svg class="sd-min-ldr" aria-hidden="true"><line stroke="var(--sd-ink)" stroke-width="1.5" stroke-dasharray="2 4" stroke-linecap="round"/></svg>${pops}`;
    this.k = 'L0';
    this.VW = b.VW;
    this.addEventListener('click', (e) => {
      const g = e.target.closest('[data-hs]');
      if (!g) return;
      // The nearest dot to the tap wins, so 44 px hit areas on thin layers do not steal each other's taps.
      let best = g, bd = 1e9;
      this.querySelectorAll('[data-hs]').forEach((d) => {
        const r = d.querySelector('.sd-min-dt').getBoundingClientRect();
        const dd = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
        if (dd < bd) { bd = dd; best = d; }
      });
      this.open((e.detail === 0 ? g : best).dataset.hs);
    });
    this.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.matches && e.target.matches('[data-hs]')) {
        e.preventDefault();
        this.open(e.target.dataset.hs);
      }
    });
    if ('ResizeObserver' in window) new ResizeObserver(() => { this.hit(); this.place(); }).observe(this);
    this.hit();
    this.open('L0');
  }
  // Each hit circle is at least 44 px across on screen, whatever the drawing's scale.
  hit() {
    const sv = this.querySelector('.sd-min-bed');
    if (!sv) return;
    const w = sv.getBoundingClientRect().width;
    if (!w) return;
    const r = Math.max(26, 22.6 / (w / this.VW));
    this.querySelectorAll('.sd-min-hit').forEach((c) => c.setAttribute('r', r.toFixed(1)));
  }
  open(k) {
    this.k = k;
    this.querySelectorAll('[data-hs]').forEach((g) => g.classList.toggle('sd-on', g.dataset.hs === k));
    this.querySelectorAll('.sd-min-pop').forEach((p) => (p.hidden = p.dataset.for !== k));
    this.place();
  }
  place() {
    const pop = this.querySelector(`.sd-min-pop[data-for="${this.k}"]`);
    const g = this.querySelector(`[data-hs="${this.k}"] .sd-min-dt`);
    const ln = this.querySelector('.sd-min-ldr line');
    if (!pop || !g) return;
    if (matchMedia('(min-width: 960px)').matches) {
      pop.style.left = pop.style.top = '';
      this.style.minHeight = '';
      return;
    }
    const sr = this.getBoundingClientRect();
    const gr = g.getBoundingClientRect();
    if (!sr.width) return;
    const cx = gr.left + gr.width / 2 - sr.left;
    const W = pop.offsetWidth;
    const left = Math.max(0, Math.min(sr.width - W, cx - W / 2));
    const sv = this.querySelector('.sd-min-bed').getBoundingClientRect();
    const top = Math.max(gr.bottom - sr.top + 16, sv.bottom - sr.top - 4);
    if (ln) {
      ln.setAttribute('x1', cx);
      ln.setAttribute('x2', cx);
      ln.setAttribute('y1', gr.bottom - sr.top + 2);
      ln.setAttribute('y2', top);
    }
    pop.style.left = left + 'px';
    pop.style.top = top + 'px';
    pop.style.setProperty('--ar', cx - left + 'px');
    this.style.minHeight = Math.ceil(top + pop.offsetHeight + 8) + 'px';
  }
}

if (!customElements.get('sunday-m-inside')) customElements.define('sunday-m-inside', SundayMInside);
